/* ============================================================
   HARDRIP V20.203 - GSC REAL REEL AUTOPLAY + SAFARI FALLBACK

   GSC attualmente rende anche i Reel come <img>. Questo fix:
   - usa GSC_INSTAFEED_MEDIA, la sorgente dati nativa del widget
   - riconosce SOLO gli elementi con mediaType === 'VIDEO'
   - abbina ogni Reel alla propria thumbnail, senza mapping per indice
   - sostituisce la sola thumbnail del Reel con il suo video reale
   - usa un fallback Shopify stabile SOLO per il Reel DNs6lVyRLZr,
     il cui URL CDN Instagram restituisce 403 su Safari/WebKit
   - mantiene intatti card, overlay, icone, popup, carousel e geometria GSC
   - forza autoplay muted loop playsinline sui Reel visibili
   - riapplica il fix se GSC ricrea/clona le card
   ============================================================ */
(() => {
  'use strict';

  if (window.__HARDRIP_GSC_FIX_V20203__) return;
  window.__HARDRIP_GSC_FIX_V20203__ = true;

  const WIDGET_SELECTOR = '.gfa-widget';
  const IMAGE_SELECTOR = 'img.gfa-media__source';
  const VIDEO_SELECTOR = 'video.gfa-media__source[data-hardrip-gsc-reel="true"]';

  const FIRST_REEL_ID = '18088869307809594';
  const FIRST_REEL_ASSET = 'HARDRIP_GSC_DNs6lVyRLZr.mp4';
  const SCRIPT_SRC = document.currentScript?.src || '';
  let FIRST_REEL_FALLBACK_URL = '';
  try {
    if (SCRIPT_SRC) FIRST_REEL_FALLBACK_URL = new URL(FIRST_REEL_ASSET, SCRIPT_SRC).href;
  } catch (_) {}

  const wired = new WeakSet();
  const observed = new WeakSet();
  let queued = false;

  const filename = (src) => {
    if (!src) return '';
    try {
      return new URL(src, location.href).pathname.split('/').pop() || '';
    } catch (_) {
      return String(src).split('?')[0].split('/').pop() || '';
    }
  };

  const mediaGroups = () =>
    Array.isArray(window.GSC_INSTAFEED_MEDIA)
      ? window.GSC_INSTAFEED_MEDIA
      : [];

  const preferredMediaUrl = (item) => {
    if (String(item?.id || '') === FIRST_REEL_ID && FIRST_REEL_FALLBACK_URL) {
      return FIRST_REEL_FALLBACK_URL;
    }
    return typeof item?.mediaUrl === 'string' ? item.mediaUrl : '';
  };

  function buildMediaIndex() {
    const byPoster = new Map();
    const byId = new Map();

    for (const group of mediaGroups()) {
      const widgetId = String(group?.id || '');
      const items = Array.isArray(group?.data) ? group.data : [];
      if (!widgetId) continue;

      for (const item of items) {
        const mediaUrl = preferredMediaUrl(item);
        if (
          item?.mediaType !== 'VIDEO' ||
          !mediaUrl ||
          typeof item.thumbnailUrl !== 'string' ||
          !item.thumbnailUrl
        ) {
          continue;
        }

        const record = { widgetId, item, mediaUrl };
        const posterKey = filename(item.thumbnailUrl);
        if (posterKey) byPoster.set(`${widgetId}::${posterKey}`, record);
        if (item.id) byId.set(String(item.id), record);
      }
    }

    return { byPoster, byId };
  }

  const widgetIdFor = (element) => {
    const root = element?.closest?.(WIDGET_SELECTOR);
    if (!root) return '';

    for (const group of mediaGroups()) {
      const id = String(group?.id || '');
      if (id && root.classList.contains(id)) return id;
    }

    return '';
  };

  const visible = (video) => {
    if (!video?.isConnected) return false;
    const rect = video.getBoundingClientRect();
    return (
      rect.width > 8 &&
      rect.height > 8 &&
      rect.bottom > 0 &&
      rect.top < window.innerHeight &&
      rect.right > 0 &&
      rect.left < window.innerWidth
    );
  };

  function configure(video) {
    if (!video?.isConnected) return;

    video.muted = true;
    video.defaultMuted = true;
    video.autoplay = true;
    video.loop = true;
    video.playsInline = true;
    video.controls = false;
    video.preload = 'auto';

    video.setAttribute('muted', '');
    video.setAttribute('autoplay', '');
    video.setAttribute('loop', '');
    video.setAttribute('playsinline', '');
    video.setAttribute('webkit-playsinline', '');
    video.setAttribute('preload', 'auto');
    video.removeAttribute('controls');

    try { video.volume = 0; } catch (_) {}
  }

  function play(video) {
    if (!video?.isConnected || document.hidden || !visible(video)) return;
    configure(video);

    try {
      const result = video.play();
      result?.catch?.(() => {});
    } catch (_) {}
  }

  function wire(video) {
    if (!video) return;
    configure(video);
    if (wired.has(video)) return;
    wired.add(video);

    for (const eventName of ['loadedmetadata', 'loadeddata', 'canplay', 'canplaythrough']) {
      video.addEventListener(eventName, () => play(video));
    }

    video.addEventListener('ended', () => {
      try { video.currentTime = 0; } catch (_) {}
      play(video);
    });

    video.addEventListener('pause', () => {
      if (!document.hidden && visible(video)) {
        window.setTimeout(() => play(video), 60);
      }
    });

    video.addEventListener('stalled', () => {
      if (visible(video)) window.setTimeout(() => play(video), 120);
    });

    video.addEventListener('suspend', () => {
      if (visible(video)) window.setTimeout(() => play(video), 120);
    });

    video.addEventListener('error', () => {
      window.setTimeout(queueScan, 300);
    });
  }

  const intersectionObserver = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        const video = entry.target;
        if (entry.isIntersecting && visible(video)) {
          play(video);
        } else if (!visible(video)) {
          try { video.pause(); } catch (_) {}
        }
      }
    },
    {
      root: null,
      rootMargin: '180px 0px 180px 0px',
      threshold: [0, 0.01, 0.2, 0.6]
    }
  );

  function observe(video) {
    if (observed.has(video)) return;
    observed.add(video);
    intersectionObserver.observe(video);
  }

  function replaceReelImage(img, record) {
    if (!img?.isConnected || !record?.mediaUrl) return null;

    const item = record.item;
    const mediaUrl = record.mediaUrl;
    const poster = img.currentSrc || img.getAttribute('src') || item.thumbnailUrl || '';
    const video = document.createElement('video');

    video.className = img.className;
    if (img.getAttribute('style')) video.setAttribute('style', img.getAttribute('style'));
    if (img.getAttribute('alt')) video.setAttribute('aria-label', img.getAttribute('alt'));

    video.dataset.hardripGscReel = 'true';
    video.dataset.hardripGscWidget = record.widgetId;
    video.dataset.hardripGscMediaId = String(item.id || '');
    video.dataset.hardripGscMediaUrl = mediaUrl;
    video.dataset.hardripGscPosterKey = filename(poster);
    if (String(item.id || '') === FIRST_REEL_ID) {
      video.dataset.hardripGscFallback = 'shopify';
    }

    configure(video);
    if (poster) video.poster = poster;
    video.src = mediaUrl;
    video.setAttribute('src', mediaUrl);

    img.replaceWith(video);
    wire(video);
    observe(video);
    play(video);

    return video;
  }

  function refreshExistingVideo(video, record) {
    if (!video?.isConnected || !record?.item || !record?.mediaUrl) return;
    const item = record.item;
    const mediaUrl = record.mediaUrl;

    configure(video);
    wire(video);
    observe(video);

    if (String(item.id || '') === FIRST_REEL_ID) {
      video.dataset.hardripGscFallback = 'shopify';
    }

    if (video.dataset.hardripGscMediaUrl !== mediaUrl) {
      video.dataset.hardripGscMediaUrl = mediaUrl;
      video.src = mediaUrl;
      video.setAttribute('src', mediaUrl);
      try { video.load(); } catch (_) {}
    }

    play(video);
  }

  function scan() {
    const index = buildMediaIndex();
    if (!index.byPoster.size) return;

    document.querySelectorAll(IMAGE_SELECTOR).forEach((img) => {
      const widgetId = widgetIdFor(img);
      if (!widgetId) return;

      const posterKey = filename(img.currentSrc || img.getAttribute('src') || '');
      if (!posterKey) return;

      const record = index.byPoster.get(`${widgetId}::${posterKey}`);
      if (!record) return;

      replaceReelImage(img, record);
    });

    document.querySelectorAll(VIDEO_SELECTOR).forEach((video) => {
      const mediaId = video.dataset.hardripGscMediaId || '';
      const record = index.byId.get(mediaId);
      if (record) refreshExistingVideo(video, record);
      else {
        configure(video);
        wire(video);
        observe(video);
        play(video);
      }
    });
  }

  function queueScan() {
    if (queued) return;
    queued = true;
    requestAnimationFrame(() => {
      queued = false;
      scan();
    });
  }

  const mutationObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      if (mutation.type === 'childList' && (mutation.addedNodes.length || mutation.removedNodes.length)) {
        queueScan();
        return;
      }
    }
  });

  function start() {
    scan();

    mutationObserver.observe(document.documentElement, {
      subtree: true,
      childList: true
    });

    /* GSC muove il track anche senza modifiche childList. */
    window.setInterval(scan, 500);

    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) window.setTimeout(scan, 40);
    });

    window.addEventListener('pageshow', () => window.setTimeout(scan, 50), { passive: true });
    window.addEventListener('focus', () => window.setTimeout(scan, 40), { passive: true });
    window.addEventListener('scroll', queueScan, { passive: true });
    window.addEventListener('resize', queueScan, { passive: true });

    document.addEventListener('pointerup', () => {
      window.setTimeout(scan, 0);
      window.setTimeout(scan, 120);
      window.setTimeout(scan, 350);
    }, true);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();

/* ============================================================
   HARDRIP V20.211 - RESTORE GSC STOCK PORTRAIT CARD GEOMETRY

   IMPORTANT: this does not change media fit, borders, radius, overlays,
   arrows or carousel behavior. It only restores the card proportion
   visible in the user's reference screenshot (3:4 width:height).
   GSC keeps its own native media rendering, including its own object-fit.
   Reel playback/recovery above remains untouched.
   ============================================================ */
(() => {
  'use strict';

  if (window.__HARDRIP_GSC_STOCK_GEOMETRY_V20211__) return;
  window.__HARDRIP_GSC_STOCK_GEOMETRY_V20211__ = true;

  const STYLE_ID = 'hardrip-gsc-stock-portrait-geometry-v20211';

  function installStyle() {
    if (document.getElementById(STYLE_ID)) return;
    const style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent = `
      html body .gfa-widget .gfa-carousel .gfa-media::before {
        padding-top: 133.333333% !important;
      }
    `;
    (document.head || document.documentElement).appendChild(style);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', installStyle, { once: true });
  } else {
    installStyle();
  }
})();
